import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import InfiniteLoader from "react-window-infinite-loader";
import useSetState from "../../hooks/useSetState";
import Event from "./Event";
import { getTelemetryByDevice } from "../../apiCalls/config/devices";

import { setAlert } from "../../redux/actions/actions";

const useStyles = makeStyles(() => ({
  root: {
    height: "calc(100vh - 200px)",
  },
}));

interface Props {
  imei: string;
  from: { date: Date; time: string };
  to: { date: Date; time: string };
  initialEvents: any;
  loading: boolean;
}

const EventsList = ({ imei, from, to, initialEvents, loading }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [
    { events, indexOpen, pagesLoaded, hasNextPage, isNextPageLoading },
    setState,
  ] = useSetState({
    events: [],
    indexOpen: [],
    pagesLoaded: 1,
    hasNextPage: true,
    isNextPageLoading: false,
  });

  const loadNextPage = (): any => {
    setState({ isNextPageLoading: true });
    getTelemetryByDevice(
      imei,
      pagesLoaded + 1,
      from.date.toISOString().slice(0, 10),
      to.date.toISOString().slice(0, 10),
      from.time,
      to.time,
    )
      .then((res) => {
        if (res && res.data) {
          setState({
            events: [...events].concat(res.data),
            isNextPageLoading: false,
            pagesLoaded: pagesLoaded + 1,
          });
        }
      })
      .catch((e) => {
        dispatch(setAlert(e.detail, "error"));
      });
  };

  useEffect(() => {
    if (initialEvents.length) {
      setState({ events: initialEvents, pagesLoaded: 1 });
    }
  }, [initialEvents]);

  const itemCount = hasNextPage ? events.length + 1 : events.length;
  const loadMoreItems = isNextPageLoading ? () => {} : loadNextPage;
  const isItemLoaded = (index) => !hasNextPage || index < events.length;

  const myRef = React.useRef();

  const handleClick = (index, expanded) => {
    if (expanded) {
      setState({ indexOpen: [...indexOpen, index] });
    } else {
      setState({ indexOpen: indexOpen.filter((el) => el !== index) });
    }
  };

  useEffect(() => {
    if (myRef.current) {
      // @ts-ignore
      myRef.current?.resetAfterIndex(0);
    }
  }, [indexOpen]);

  const calculateSize = (index) => (indexOpen.includes(index) ? 410 : 74);

  const isOpen = (index) => indexOpen.includes(index);

  const Row = ({ index, style }) => (
    <div style={style}>
      <Event
        event={events[index]}
        index={index}
        handleClick={handleClick}
        open={isOpen(index)}
      />
    </div>
  );

  const handleRender = () => {
    if (loading) {
      return <div>Loading...</div>;
    }
    if (!initialEvents.length) {
      return <div>No data for the selected time period</div>;
    }
    return (
      <AutoSizer>
        {({ height, width }) => (
          <InfiniteLoader
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            isItemLoaded={isItemLoaded}
          >
            {({ onItemsRendered, ref }) => (
              <VariableSizeList
                height={height}
                itemCount={itemCount}
                itemSize={(index) => calculateSize(index)}
                width={width}
                estimatedItemSize={80}
                ref={(list) => {
                  // @ts-ignore move infinite loader to the library to get rid from this issue
                  ref(list);
                  // @ts-ignore
                  myRef.current = list;
                }}
                onItemsRendered={onItemsRendered}
              >
                {Row}
              </VariableSizeList>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    );
  };

  return <div className={classes.root}>{handleRender()}</div>;
};

export default EventsList;
